import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, X } from "@phosphor-icons/react";

const TYPES = [
  ["warehouse", "Warehouse"],
  ["project_site", "Project Site"],
  ["office", "Office"],
  ["fixed_asset", "Fixed Asset"],
  ["other", "Other"],
];

export default function LocationMasterPage() {
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "", name: "", address: "", hz_location: "",
    location_type: "warehouse", mapped_site_id: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/alm/locations"), api.get("/alm/sites")]);
    setRows(a.data); setSites(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/alm/locations", { ...form, mapped_site_id: form.mapped_site_id || null });
    setOpen(false);
    setForm({ code: "", name: "", address: "", hz_location: "", location_type: "warehouse", mapped_site_id: "" });
    load();
  };

  const remove = async (id) => { if (window.confirm("Delete this location?")) { await api.delete(`/alm/locations/${id}`); load(); } };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/alm/locations/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
    alert(`Imported ${data.imported}, skipped ${data.skipped}${data.errors?.length ? `\nErrors:\n${data.errors.join("\n")}` : ""}`);
    e.target.value = "";
    load();
  };

  const siteName = (sid) => sites.find(s => s.id === sid)?.site_number || "";

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="Location Master"
        subtitle="Inventory, project, office and fixed-asset locations — each mapped to a Site."
        testid="alm-locations-title"
        actions={
          <>
            <label data-testid="alm-location-import-btn" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 inline-flex items-center gap-2 cursor-pointer">
              Import CSV
              <input type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="alm-location-import-input" />
            </label>
            <button onClick={() => setOpen(true)} data-testid="alm-location-new-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
              <Plus size={14} weight="bold" /> New Location
            </button>
          </>
        }
      />
      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="alm-locations-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["Code", "Name", "Type", "Mapped Site", "HZ Location", "Address", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold">{r.code}</td>
                  <td className="px-4 py-3 font-semibold">{r.name}</td>
                  <td className="px-4 py-3 text-xs uppercase">{r.location_type?.replace("_", " ")}</td>
                  <td className="px-4 py-3 font-mono text-xs">{siteName(r.mapped_site_id)}</td>
                  <td className="px-4 py-3 text-xs">{r.hz_location}</td>
                  <td className="px-4 py-3 text-xs">{r.address}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(r.id)} data-testid={`alm-location-del-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">No locations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={e => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="alm-location-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Create</div>
                <h2 className="text-2xl font-black tracking-tight">New Location</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {[
                ["code", "Location Code *", true],
                ["name", "Name *", true],
                ["address", "Address", false],
                ["hz_location", "HZ Location", false],
              ].map(([k, label, required]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type="text" required={required} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`alm-location-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
                </div>
              ))}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Type</label>
                <select value={form.location_type} onChange={e => setForm({ ...form, location_type: e.target.value })} data-testid="alm-location-type" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Mapped Site</label>
                <select value={form.mapped_site_id} onChange={e => setForm({ ...form, mapped_site_id: e.target.value })} data-testid="alm-location-site" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="">— None —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.site_number} — {s.name}</option>)}
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="alm-location-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Create Location</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
